import { SerializedError, createSlice, isAnyOf } from '@reduxjs/toolkit';

import { REQUEST_METHODS } from '@lib/core/service/consts';
import { createTypedAsyncThunk } from '@lib/core/service/createTypedAsyncThunk';
import { request } from '@lib/core/service/requests/request';
import { profileDataApiUrlCreator } from '@lib/core/users/slices/urls';
import { IUpdateProfileRequestBody, TProfile, TProfileOptions } from '@lib/core/users/types';

export interface IProfileState {
  data: undefined | TProfile;
  options: TProfileOptions;
  isLoading: boolean;
  error: SerializedError;
}

export const initialState: IProfileState = {
  data: undefined,
  error: null,
  isLoading: false,
  options: undefined,
};

export const actionGetProfileData = createTypedAsyncThunk('actionGetProfileData', async () =>
  request({ url: profileDataApiUrlCreator() }),
);

export const actionUpdateProfileData = createTypedAsyncThunk(
  'users/profile/actionUpdateProfileData',
  async (requestBody: IUpdateProfileRequestBody) =>
    request({
      body: requestBody,
      method: REQUEST_METHODS.PATCH,
      url: profileDataApiUrlCreator(),
    }),
);

export const actionGetProfileOptionsData = createTypedAsyncThunk(
  'users/profile/actionGetProfileOptionsData',
  async () => request({ method: REQUEST_METHODS.OPTIONS, url: profileDataApiUrlCreator() }),
);

export const profileSlice = createSlice({
  extraReducers: builder => {
    builder.addCase(actionGetProfileOptionsData.fulfilled, (state, { payload }) => {
      state.isLoading = false;
      state.error = null;
      state.options = payload?.choices;
    });
    builder.addMatcher(
      isAnyOf(actionUpdateProfileData.pending, actionGetProfileData.pending, actionGetProfileOptionsData.pending),
      state => {
        state.isLoading = true;
        state.error = null;
      },
    );
    builder.addMatcher(
      isAnyOf(actionUpdateProfileData.fulfilled, actionGetProfileData.fulfilled),
      (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
        state.data = payload;
      },
    );
    builder.addMatcher(
      isAnyOf(actionUpdateProfileData.rejected, actionGetProfileData.rejected, actionGetProfileOptionsData.rejected),
      (state, action) => {
        state.error = action.error;
        state.isLoading = false;
      },
    );
  },
  initialState,
  name: 'profile',
  reducers: {
    actionResetUserProfile: () => initialState,
    actionSetUserProfileId: (state, { payload }: { payload: string }) => {
      state.data = { ...state.data, identifier: payload || null };
    },
  },
});

export const { actionSetUserProfileId, actionResetUserProfile } = profileSlice.actions;

export default profileSlice.reducer;
